import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API_BASE_URL from "../config/apiConfig";
import { apiFetch } from "../config/apiClient";
import { clearAuth, getUser } from "../config/auth";
import {
  MASTER_JOB_LABELS,
  masterJobsActionApi,
  masterJobsReadApi,
} from "../config/masterJobsApi";
import { JobDetailsView } from "../Post/postnew";
import Toast from "../Toast/Toast";
import "./CurrentAffairsQuizAdmin.css";

const ADMIN_PAGES = [
  { key: "quiz", label: "Quiz", type: "quiz" },
  { key: "questions", label: "Questions", type: "question" },
  { key: "jobs", label: "Jobs", type: "job" },
  { key: "users", label: "Users", type: "user" },
];

const JOB_SECTIONS = [
  { key: "latest-jobs", label: "Latest Jobs", type: "job", jobLabel: MASTER_JOB_LABELS.latestJobs },
  { key: "latest-results", label: "Latest Results", type: "job", jobLabel: MASTER_JOB_LABELS.latestResults },
  { key: "admit-cards", label: "Admit Cards", type: "job", jobLabel: MASTER_JOB_LABELS.admitCards },
  { key: "admission", label: "Admission", type: "job", jobLabel: MASTER_JOB_LABELS.admission },
  { key: "answer-keys", label: "Answer Keys", type: "job", jobLabel: MASTER_JOB_LABELS.answerKeys },
];

const slugify = (title = "") =>
  String(title)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const makeSlugWithId = (title, id) => `${slugify(title || "post")}-${id}`;

const parseIdFromSlug = (slug) => {
  try {
    const idText = String(slug || "").substring(String(slug || "").lastIndexOf("-") + 1);
    const id = Number(idText);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
};

const readQuizId = (item) => item?.id ?? item?.quizId ?? null;
const readJobId = (item) => item?.id ?? item?.jobId ?? parseIdFromSlug(item?.slugurl) ?? null;
const readUserId = (item) => item?.id ?? item?.userId ?? null;
const readQuestionId = (item) => item?.id ?? null;

const getApiErrorMessage = (error, fallbackMessage = "Unexpected error occurred") =>
  error?.data?.message || error?.data?.error || error?.message || fallbackMessage;

const createHttpError = (status, data, fallbackMessage) => {
  const error = new Error(data?.message || data?.error || fallbackMessage);
  error.status = status;
  error.data = data;
  return error;
};

const normalizeBulkQuestionsPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.questions)) return payload.questions;
  if (payload && typeof payload === "object") return [payload];
  return [];
};

const validateBulkQuestion = (question) => {
  if (!question || typeof question !== "object") return "Questions list is required";
  if (!String(question?.questionCode || "").trim()) return "Question code is required";
  if (!String(question?.subject || "").trim()) return "Subject is required";
  if (!String(question?.topic || "").trim()) return "Topic is required";
  if (!String(question?.questionText || "").trim()) return "Question text is required";
  if (!String(question?.type || "").trim()) return "Question type is required";
  if (!Array.isArray(question?.choices) || question.choices.length === 0) return "Choices are required";
  if (
    !Array.isArray(question?.correctAnswer?.choiceIds) ||
    question.correctAnswer.choiceIds.length === 0
  ) {
    return "Correct answer is required";
  }
  return "";
};

const formatQuestionMetaLabel = (value, fallback = "NA") => {
  const text = String(value || "").trim();
  return text || fallback;
};

const getQuestionChoices = (question) =>
  Array.isArray(question?.choices)
    ? question.choices
    : Array.isArray(question?.options)
    ? question.options
    : [];

const getQuestionChoiceLabel = (choice, index) => {
  if (choice && typeof choice === "object") {
    return (
      choice?.choiceText ||
      choice?.text ||
      choice?.optionText ||
      choice?.label ||
      `Choice ${index + 1}`
    );
  }
  return String(choice || `Choice ${index + 1}`);
};

const getQuestionCorrectAnswerSummary = (question) => {
  const choices = getQuestionChoices(question);
  const choiceIds = Array.isArray(question?.correctAnswer?.choiceIds)
    ? question.correctAnswer.choiceIds.map((id) => String(id))
    : [];
  const correctTexts = choices
    .filter((choice) => choice && typeof choice === "object")
    .filter((choice) => choiceIds.includes(String(choice?.id ?? choice?.choiceId ?? "")))
    .map((choice, index) => getQuestionChoiceLabel(choice, index));

  if (correctTexts.length) return correctTexts.join(", ");

  const directAnswer =
    question?.correctAnswer?.text ||
    question?.correctAnswerText ||
    question?.answer ||
    question?.correctOption;

  return formatQuestionMetaLabel(directAnswer);
};

const getQuestionExplanationText = (question) =>
  String(
    question?.explanation?.text ||
      question?.explanationText ||
      question?.details_description ||
      ""
  ).trim();

const getQuestionExplanationDiagram = (question) => ({
  diagramType: question?.explanation?.diagramType || null,
  diagramData: question?.explanation?.diagramData || null,
});

const looksLikeAsciiExplanationBlock = (block) => {
  const lines = String(block || "")
    .split("\n")
    .map((line) => line.trimEnd())
    .filter(Boolean);

  if (lines.length < 2) return false;

  return lines.some((line) => /[<>^|_\\/`~\-[\]{}()+=*]|[↑↓←→]/.test(line));
};

const normalizeDiagramSegment = (segment) => {
  const x1 = Number(segment?.x1) || 0;
  const y1 = Number(segment?.y1) || 0;
  let x2 = Number(segment?.x2);
  let y2 = Number(segment?.y2);
  const hasTarget = Number.isFinite(x2) && Number.isFinite(y2);
  const declaredDistance = Number(segment?.distance);
  const distanceUnits = declaredDistance > 0 ? declaredDistance : 1;
  const minVisualLength = 20;
  const visualLength = Math.max(distanceUnits * 12, minVisualLength);
  const direction = String(segment?.direction || "")
    .trim()
    .toUpperCase();

  if (!hasTarget || (Math.abs(x2 - x1) < 0.5 && Math.abs(y2 - y1) < 0.5)) {
    switch (direction) {
      case "N":
        x2 = x1;
        y2 = y1 - visualLength;
        break;
      case "S":
        x2 = x1;
        y2 = y1 + visualLength;
        break;
      case "E":
        x2 = x1 + visualLength;
        y2 = y1;
        break;
      case "W":
        x2 = x1 - visualLength;
        y2 = y1;
        break;
      case "NE":
        x2 = x1 + visualLength * 0.7;
        y2 = y1 - visualLength * 0.7;
        break;
      case "NW":
        x2 = x1 - visualLength * 0.7;
        y2 = y1 - visualLength * 0.7;
        break;
      case "SE":
        x2 = x1 + visualLength * 0.7;
        y2 = y1 + visualLength * 0.7;
        break;
      case "SW":
        x2 = x1 - visualLength * 0.7;
        y2 = y1 + visualLength * 0.7;
        break;
      default:
        x2 = x1 + visualLength;
        y2 = y1;
        break;
    }
  }

  return {
    ...segment,
    x1,
    y1,
    x2,
    y2,
  };
};

const buildFallbackDirectionSegment = (data) => {
  const startPoint = data?.startPoint || (Array.isArray(data?.points) ? data.points[0] : null);
  const finalDirection = String(data?.finalDirection || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace("उत्तर", "N")
    .replace("दक्षिण", "S")
    .replace("पूर्व", "E")
    .replace("पश्चिम", "W")
    .replace(/-/g, "");

  if (!startPoint || !finalDirection) return null;

  return normalizeDiagramSegment({
    x1: Number(startPoint?.x) || 0,
    y1: Number(startPoint?.y) || 0,
    x2: Number(startPoint?.x) || 0,
    y2: Number(startPoint?.y) || 0,
    direction: finalDirection,
    distance: 1,
    label: data?.finalDirection || "",
  });
};

function AdminDirectionSenseSvgDiagram({ data }) {
  const canvas = data?.canvas || {};
  const width = Math.max(160, Number(canvas?.width) || 320);
  const height = Math.max(140, Number(canvas?.height) || 220);
  const rawSegments = Array.isArray(data?.segments) ? data.segments.map(normalizeDiagramSegment) : [];
  const fallbackSegment = rawSegments.length ? null : buildFallbackDirectionSegment(data);
  const segments = fallbackSegment ? [fallbackSegment] : rawSegments;
  const points = Array.isArray(data?.points) ? data.points : [];
  const compass = data?.compass || null;

  if (!segments.length && !points.length) return null;

  return (
    <div className="ca-admin-question-diagram-shell">
      <svg
        className="ca-admin-question-diagram"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Direction sense explanation diagram"
      >
        <defs>
          <marker
            id="admin-direction-arrow"
            markerWidth="6"
            markerHeight="6"
            refX="5"
            refY="3"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M 0 0 L 6 3 L 0 6 z" fill="#0d47a1" />
          </marker>
        </defs>
        <rect x="0" y="0" width={width} height={height} rx="18" fill="#f8fbff" />
        {compass?.show ? (
          <g transform={`translate(${compass?.x || width - 50} ${compass?.y || 32})`}>
            <circle r="18" fill="#fff" stroke="#b9cbe7" strokeWidth="1.5" />
            <line x1="0" y1="-12" x2="0" y2="12" stroke="#89a3c8" strokeWidth="1.5" />
            <line x1="-12" y1="0" x2="12" y2="0" stroke="#89a3c8" strokeWidth="1.5" />
            <text x="0" y="-18" textAnchor="middle">N</text>
            <text x="0" y="30" textAnchor="middle">S</text>
            <text x="22" y="5" textAnchor="middle">E</text>
            <text x="-22" y="5" textAnchor="middle">W</text>
          </g>
        ) : null}
        {segments.map((segment, index) => {
          const midX = (Number(segment?.x1) + Number(segment?.x2)) / 2;
          const midY = (Number(segment?.y1) + Number(segment?.y2)) / 2;
          return (
            <g key={`segment-${index}`}>
              <line
                x1={segment?.x1}
                y1={segment?.y1}
                x2={segment?.x2}
                y2={segment?.y2}
                stroke="#0d47a1"
                strokeWidth="2.5"
                strokeLinecap="round"
                markerEnd="url(#admin-direction-arrow)"
              />
              {segment?.label ? (
                <text x={midX} y={midY - 8} textAnchor="middle" className="ca-admin-question-diagram-distance">
                  {segment.label}
                </text>
              ) : null}
            </g>
          );
        })}
        {points.map((point, index) => (
          <g key={`point-${index}`}>
            <circle
              cx={point?.x}
              cy={point?.y}
              r="3.5"
              fill={index === points.length - 1 ? "#2eaf53" : "#c62828"}
            />
            {point?.label ? (
              <text
                x={(Number(point?.x) || 0) + 10}
                y={(Number(point?.y) || 0) - 10}
                className="ca-admin-question-diagram-point"
              >
                {point.label}
              </text>
            ) : null}
          </g>
        ))}
      </svg>
      {data?.finalDirection || data?.finalAnswerLabel ? (
        <div className="ca-admin-question-diagram-meta">
          {data?.finalDirection ? <span>{`Final Direction: ${data.finalDirection}`}</span> : null}
          {data?.finalAnswerLabel ? <span>{`Answer: ${data.finalAnswerLabel}`}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

function QuestionExplanationPreview({ text, diagramType, diagramData }) {
  const blocks = String(text || "")
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);

  if (!blocks.length && !(diagramType === "DIRECTION_SENSE_SVG" && diagramData)) return null;

  return (
    <div className="ca-admin-question-explanation-shell">
      <h4>Explanation</h4>
      <div className="ca-admin-question-explanation-body">
        {blocks.map((block, index) =>
          looksLikeAsciiExplanationBlock(block) ? (
            <pre key={`exp-${index}`} className="ca-admin-question-explanation-ascii">
              {block}
            </pre>
          ) : (
            <p key={`exp-${index}`} className="ca-admin-question-explanation-paragraph">
              {block}
            </p>
          )
        )}
        {diagramType === "DIRECTION_SENSE_SVG" && diagramData ? (
          <AdminDirectionSenseSvgDiagram data={diagramData} />
        ) : null}
      </div>
    </div>
  );
}

const applyPreviewQuestionsToPayload = (originalPayload, questions) => {
  if (Array.isArray(originalPayload)) {
    return questions;
  }

  if (originalPayload && typeof originalPayload === "object") {
    if (Array.isArray(originalPayload?.questions)) {
      return { ...originalPayload, questions };
    }

    return questions.length === 1 ? questions[0] : { ...originalPayload, questions };
  }

  return questions;
};

const buildBulkUploadMessage = (data) => {
  const total = Number(data?.total || 0);
  const created = Number(data?.created || 0);
  const updated = Number(data?.updated || 0);

  if (created > 0 && updated === 0) {
    return `Upload complete: ${created}/${total} questions created.`;
  }
  if (created > 0 && updated > 0) {
    return `Upload complete: ${created} created, ${updated} updated (duplicates).`;
  }
  if (created === 0 && updated > 0) {
    return `No new questions. ${updated} existing questions were updated.`;
  }
  return data?.message || "Upload complete.";
};

const tryParseJson = (value) => {
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const getEditableJobContent = (data) => {
  const parsedContent = tryParseJson(data?.content);

  if (parsedContent && typeof parsedContent === "object" && !Array.isArray(parsedContent)) {
    return parsedContent;
  }

  if (data && typeof data === "object" && !Array.isArray(data)) {
    const {
      id,
      jobId,
      slugWithId,
      slugurl,
      createdAt,
      updatedAt,
      approved,
      label,
      content,
      ...rest
    } = data;

    return Object.keys(rest).length ? rest : data;
  }

  return {};
};

const normalizeFilterValue = (value) => String(value || "").trim().toLowerCase();
const defaultJobJson = '{\n  "title": "New Post Title",\n  "short_description": "Write details here"\n}';

const buildJobPreviewPayload = ({ contentObject, title, advertisementNo }) => {
  const payload =
    contentObject && typeof contentObject === "object" && !Array.isArray(contentObject)
      ? { ...contentObject }
      : {};

  if (title && !payload.title) payload.title = title;
  if (advertisementNo && !payload.advertisement_no && !payload.advertisementNo) {
    payload.advertisement_no = advertisementNo;
  }

  return payload;
};

function CurrentAffairsQuizAdmin() {
  const navigate = useNavigate();
  const { adminSection, jobSection } = useParams();
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [schedulerEnabled, setSchedulerEnabled] = useState(false);
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  const [schedulerUpdating, setSchedulerUpdating] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const [title, setTitle] = useState("");
  const [createdAt, setCreatedAt] = useState("");
  const [advertisementNo, setAdvertisementNo] = useState("");
  const [contentText, setContentText] = useState(
    '{\n  "title": "New Post Title",\n  "short_description": "Write details here"\n}'
  );

  const [userUsername, setUserUsername] = useState("");
  const [userEmailId, setUserEmailId] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [userRole, setUserRole] = useState("STUDENT");
  const [userPassword, setUserPassword] = useState("");
  const [jsonUploadKey, setJsonUploadKey] = useState(0);
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [scrapeLabel, setScrapeLabel] = useState("");
  const [scrapeForce, setScrapeForce] = useState(false);
  const [autoForce, setAutoForce] = useState(false);
  const [autoMaxLinks, setAutoMaxLinks] = useState("0");
  const [autoMaxPerCategory, setAutoMaxPerCategory] = useState("1");
  const [selectedQuestionSubject, setSelectedQuestionSubject] = useState("");
  const [selectedQuestionTopic, setSelectedQuestionTopic] = useState("");
  const [originalJobContentText, setOriginalJobContentText] = useState("");
  const [isJobPreviewOpen, setIsJobPreviewOpen] = useState(false);
  const [jobPreviewData, setJobPreviewData] = useState(null);
  const [editingPreviewSection, setEditingPreviewSection] = useState(null);
  const [previewSectionText, setPreviewSectionText] = useState("");
  const [previewSectionError, setPreviewSectionError] = useState("");
  const [isQuestionPreviewOpen, setIsQuestionPreviewOpen] = useState(false);
  const [questionPreviewItems, setQuestionPreviewItems] = useState([]);
  const [editingPreviewQuestionIndex, setEditingPreviewQuestionIndex] = useState(null);
  const [previewQuestionText, setPreviewQuestionText] = useState("");
  const [previewQuestionError, setPreviewQuestionError] = useState("");

  const activeAdminPage = useMemo(
    () => ADMIN_PAGES.find((page) => page.key === adminSection) || ADMIN_PAGES[0],
    [adminSection]
  );

  const selectedSection = useMemo(
    () =>
      activeAdminPage.key === "jobs"
        ? JOB_SECTIONS.find((section) => section.key === jobSection) || JOB_SECTIONS[0]
        : activeAdminPage,
    [activeAdminPage, jobSection]
  );
  const isQuizMode = selectedSection.type === "quiz";
  const isQuestionMode = selectedSection.type === "question";
  const isUserMode = selectedSection.type === "user";
  const isJobMode = !isQuizMode && !isQuestionMode && !isUserMode;

  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedId) || null,
    [items, selectedId]
  );

  const visibleItems = useMemo(() => {
    if (!isQuestionMode) return items;
    if (!selectedQuestionSubject || !selectedQuestionTopic) return [];

    return items.filter((item) => {
      const subject = normalizeFilterValue(item?.raw?.subject);
      const topic = normalizeFilterValue(item?.raw?.topic);
      return (
        subject === normalizeFilterValue(selectedQuestionSubject) &&
        topic === normalizeFilterValue(selectedQuestionTopic)
      );
    });
  }, [isQuestionMode, items, selectedQuestionSubject, selectedQuestionTopic]);

  const questionSubjects = useMemo(
    () =>
      Array.from(
        new Set(
          items
            .map((item) => String(item?.raw?.subject || "").trim())
            .filter(Boolean)
        )
      ).sort((left, right) => left.localeCompare(right)),
    [items]
  );

  const questionTopics = useMemo(() => {
    if (!selectedQuestionSubject) return [];

    return Array.from(
      new Set(
        items
          .filter(
            (item) =>
              normalizeFilterValue(item?.raw?.subject) ===
              normalizeFilterValue(selectedQuestionSubject)
          )
          .map((item) => String(item?.raw?.topic || "").trim())
          .filter(Boolean)
      )
    ).sort((left, right) => left.localeCompare(right));
  }, [items, selectedQuestionSubject]);

  const hasSelectedExistingJob = isJobMode && Boolean(selectedItem);
  const hasJobJsonChanges =
    hasSelectedExistingJob && contentText.trim() !== originalJobContentText.trim();

  useEffect(() => {
    const parsedUser = getUser();
    if (!parsedUser) {
      clearAuth();
      navigate("/login");
      return;
    }

    const role = String(parsedUser?.role || "STUDENT").toUpperCase();
    if (role !== "ADMIN") {
      navigate("/dashboard");
    }
  }, [navigate]);

  useEffect(() => {
    if (!adminSection) {
      navigate("/admin/dashboard/quiz", { replace: true });
      return;
    }

    const isValidAdminPage = ADMIN_PAGES.some((page) => page.key === adminSection);
    if (!isValidAdminPage) {
      navigate("/admin/dashboard/quiz", { replace: true });
      return;
    }

    if (adminSection === "jobs") {
      const isValidJobSection = JOB_SECTIONS.some((section) => section.key === jobSection);
      if (!isValidJobSection) {
        navigate(`/admin/dashboard/jobs/${JOB_SECTIONS[0].key}`, { replace: true });
      }
    }
  }, [adminSection, jobSection, navigate]);

  useEffect(() => {
    if (!isJobMode) return;
    setScrapeLabel(selectedSection.jobLabel || "");
  }, [isJobMode, selectedSection.jobLabel]);

  useEffect(() => {
    if (!isQuestionMode) {
      setSelectedQuestionSubject("");
      setSelectedQuestionTopic("");
    }
  }, [isQuestionMode]);

  useEffect(() => {
    setSelectedQuestionTopic("");
  }, [selectedQuestionSubject]);

  useEffect(() => {
    if (selectedId && !visibleItems.some((item) => item.id === selectedId)) {
      setSelectedId(null);
    }
  }, [selectedId, visibleItems]);

  const redirectToAdminLogin = useCallback((loginMessage) => {
    clearAuth();
    navigate("/login", {
      replace: true,
      state: {
        loginMessage,
        redirectTo: "/admin/dashboard",
      },
    });
  }, [navigate]);

  const handleAdminPermissionError = useCallback((err, loginMessage) => {
    if (!err || ![401, 403].includes(Number(err.status))) {
      return false;
    }
    redirectToAdminLogin(
      loginMessage || "Admin permission is required to use this job scraping action."
    );
    return true;
  }, [redirectToAdminLogin]);

  const loadSchedulerStatus = useCallback(async () => {
    setSchedulerLoading(true);
    try {
      const response = await apiFetch("/api/current-affairs-quizzes/admin/scheduler/status", {
        auth: true,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw createHttpError(response.status, data, "Unable to load scheduler status.");
      }
      setSchedulerEnabled(Boolean(data?.schedulerEnabled));
    } catch (err) {
      if (
        handleAdminPermissionError(
          err,
          "Admin access is required to view daily quiz scheduler status."
        )
      ) {
        return;
      }
      setError(err.message || "Unable to load scheduler status.");
      setToastMessage(err.message || "Unable to load scheduler status.");
    } finally {
      setSchedulerLoading(false);
    }
  }, [handleAdminPermissionError]);

  const updateSchedulerStatus = useCallback(async (nextEnabled) => {
    setSchedulerUpdating(true);
    setError("");
    setMessage("");
    try {
      const response = await apiFetch(
        nextEnabled
          ? "/api/current-affairs-quizzes/admin/scheduler/enable"
          : "/api/current-affairs-quizzes/admin/scheduler/disable",
        {
          method: "POST",
          auth: true,
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw createHttpError(response.status, data, "Unable to update scheduler status.");
      }
      const isEnabled = Boolean(data?.schedulerEnabled);
      const feedbackMessage =
        data?.message || `Daily quiz scheduler ${isEnabled ? "enabled" : "disabled"}.`;
      setSchedulerEnabled(isEnabled);
      setMessage(feedbackMessage);
      setToastMessage(feedbackMessage);
    } catch (err) {
      if (
        handleAdminPermissionError(
          err,
          "Admin access is required to manage the daily quiz scheduler."
        )
      ) {
        return;
      }
      setError(err.message || "Unable to update scheduler status.");
      setToastMessage(err.message || "Unable to update scheduler status.");
    } finally {
      setSchedulerUpdating(false);
    }
  }, [handleAdminPermissionError]);

  useEffect(() => {
    if (!isQuizMode) return;
    loadSchedulerStatus();
  }, [isQuizMode, loadSchedulerStatus]);

  const resetForm = useCallback(() => {
    setSelectedId(null);
    setTitle("");
    setCreatedAt("");
    setAdvertisementNo("");
    setContentText(defaultJobJson);
    setOriginalJobContentText("");
    setUserUsername("");
    setUserEmailId("");
    setUserPhone("");
    setUserRole("STUDENT");
    setUserPassword("");
    setScrapeUrl("");
    setScrapeForce(false);
    setIsJobPreviewOpen(false);
    setJobPreviewData(null);
    setEditingPreviewSection(null);
    setPreviewSectionText("");
    setPreviewSectionError("");
    setIsQuestionPreviewOpen(false);
    setQuestionPreviewItems([]);
    setEditingPreviewQuestionIndex(null);
    setPreviewQuestionText("");
    setPreviewQuestionError("");
  }, []);

  const normalizeList = useCallback((data) => {
    if (!Array.isArray(data)) return [];

    if (isQuizMode) {
      return data
        .map((row) => {
          const id = readQuizId(row);
          if (!id) return null;
          return {
            id,
            title: row?.title || `Quiz ${id}`,
            slugWithId: makeSlugWithId(row?.title, id),
            raw: row,
          };
        })
        .filter(Boolean);
    }

    if (isQuestionMode) {
      return data
        .map((row) => {
          const id = readQuestionId(row);
          if (!id) return null;
          const shortText = String(row?.questionText || "").trim();
          const title = row?.questionCode || (shortText ? shortText.slice(0, 70) : `Question ${id}`);
          return {
            id,
            title,
            raw: row,
          };
        })
        .filter(Boolean);
    }

    if (isUserMode) {
      return data
        .map((row) => {
          const id = readUserId(row);
          if (!id) return null;
          return {
            id,
            title: row?.username || row?.emailId || `User ${id}`,
            raw: row,
          };
        })
        .filter(Boolean);
    }

    return data
      .map((row) => {
        const id = readJobId(row);
        if (!id) return null;
        return {
          id,
          title: row?.title || `Post ${id}`,
          slugWithId: row?.slugWithId || row?.slugurl || makeSlugWithId(row?.title, id),
          raw: row,
        };
      })
      .filter(Boolean);
  }, [isQuestionMode, isQuizMode, isUserMode]);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    setError("");
    setMessage("");
    try {
      let response;
      if (isQuizMode) {
        response = await fetch(`${API_BASE_URL}/api/current-affairs-quizzes`);
      } else if (isQuestionMode) {
        response = await apiFetch("/questions", { auth: true });
      } else if (isUserMode) {
        response = await apiFetch("/users", { auth: true });
      } else {
        const data = await masterJobsReadApi.getLatest(selectedSection.jobLabel);
        setItems(normalizeList(data));
        return;
      }

      if (!response.ok) throw new Error("Failed to load list.");
      const data = await response.json();
      setItems(normalizeList(data));
    } catch (err) {
      setError(err.message || "Failed to load list.");
      setItems([]);
    } finally {
      setLoadingList(false);
    }
  }, [isQuestionMode, isQuizMode, isUserMode, normalizeList, selectedSection.jobLabel]);

  useEffect(() => {
    resetForm();
    if (isQuestionMode) {
      setContentText(
        '{\n  "questionCode": "Q-001",\n  "subject": "General Knowledge",\n  "questionText": "Sample question?",\n  "choices": [],\n  "status": "ACTIVE"\n}'
      );
    }
    loadList();
  }, [adminSection, jobSection, isQuestionMode, loadList, resetForm]);

  const parseContentOrThrow = () => {
    try {
      return JSON.parse(contentText);
    } catch {
      throw new Error("Content must be valid JSON.");
    }
  };

  const handleQuestionJsonUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError("");
    setMessage("");

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      setContentText(JSON.stringify(parsed, null, 2));
      setMessage(`Loaded JSON file: ${file.name}`);
    } catch {
      setError("Invalid JSON file. Please upload a valid .json file.");
    } finally {
      setJsonUploadKey((prev) => prev + 1);
    }
  };

  const handleOpenQuestionPreview = () => {
    try {
      const payload = parseContentOrThrow();
      const questions = normalizeBulkQuestionsPayload(payload);

      if (!questions.length) {
        throw new Error("Questions list is required");
      }

      for (const question of questions) {
        const validationMessage = validateBulkQuestion(question);
        if (validationMessage) {
          throw new Error(validationMessage);
        }
      }

      setQuestionPreviewItems(questions);
      setEditingPreviewQuestionIndex(null);
      setPreviewQuestionText("");
      setPreviewQuestionError("");
      setIsQuestionPreviewOpen(true);
    } catch (err) {
      setError(err.message || "Unable to open question preview.");
    }
  };

  const handleCloseQuestionPreview = () => {
    setIsQuestionPreviewOpen(false);
    setEditingPreviewQuestionIndex(null);
    setPreviewQuestionText("");
    setPreviewQuestionError("");
  };

  const handleEditPreviewQuestion = (index) => {
    const question = questionPreviewItems[index];
    if (!question) return;

    setEditingPreviewQuestionIndex(index);
    setPreviewQuestionText(JSON.stringify(question, null, 2));
    setPreviewQuestionError("");
  };

  const handleRemovePreviewQuestion = (index) => {
    setQuestionPreviewItems((current) => current.filter((_, itemIndex) => itemIndex !== index));

    if (editingPreviewQuestionIndex === index) {
      setEditingPreviewQuestionIndex(null);
      setPreviewQuestionText("");
      setPreviewQuestionError("");
      return;
    }

    if (
      typeof editingPreviewQuestionIndex === "number" &&
      editingPreviewQuestionIndex > index
    ) {
      setEditingPreviewQuestionIndex((current) => current - 1);
    }
  };

  const handleApplyPreviewQuestion = () => {
    if (typeof editingPreviewQuestionIndex !== "number") return;

    let nextQuestion = null;
    try {
      nextQuestion = JSON.parse(previewQuestionText);
    } catch {
      setPreviewQuestionError("Question JSON must be valid.");
      return;
    }

    const validationMessage = validateBulkQuestion(nextQuestion);
    if (validationMessage) {
      setPreviewQuestionError(validationMessage);
      return;
    }

    setQuestionPreviewItems((current) =>
      current.map((question, index) =>
        index === editingPreviewQuestionIndex ? nextQuestion : question
      )
    );
    setEditingPreviewQuestionIndex(null);
    setPreviewQuestionText("");
    setPreviewQuestionError("");
  };

  const handleApplyQuestionPreviewChanges = () => {
    if (!questionPreviewItems.length) {
      setError("At least one question is required.");
      return;
    }

    try {
      const payload = parseContentOrThrow();
      const nextPayload = applyPreviewQuestionsToPayload(payload, questionPreviewItems);
      setContentText(JSON.stringify(nextPayload, null, 2));
      setMessage("Question preview changes applied to the editor.");
      handleCloseQuestionPreview();
    } catch (err) {
      setError(err.message || "Unable to apply question preview changes.");
    }
  };

  const loadDetail = async (item) => {
    setSelectedId(item.id);
    setLoadingDetail(true);
    setError("");
    setMessage("");
    try {
      if (isUserMode) {
        const response = await apiFetch(`/users/${item.id}`, { auth: true });
        if (!response.ok) throw new Error("Failed to load user.");
        const data = await response.json();
        setUserUsername(data?.username || "");
        setUserEmailId(data?.emailId || "");
        setUserPhone(data?.phone || "");
        setUserRole(String(data?.role || "STUDENT").toUpperCase());
        setUserPassword("");
        return;
      }

      if (isQuestionMode) {
        const response = await apiFetch(`/questions/${item.id}`, { auth: true });
        if (!response.ok) throw new Error("Failed to load question.");
        const data = await response.json();
        setTitle(data?.questionCode || data?.questionText || item.title || "");
        setContentText(JSON.stringify(data, null, 2));
        setOriginalJobContentText("");
        return;
      }

      const data = isQuizMode
        ? await (async () => {
            const response = await fetch(`${API_BASE_URL}/api/current-affairs-quizzes/${item.slugWithId}`);
            if (!response.ok) throw new Error("Failed to load details.");
            return response.json();
          })()
        : await masterJobsReadApi.getById(item.id);
      if (!data) throw new Error("No data found.");

      if (isQuizMode) {
        setTitle(data?.title || item.title || "");
        setCreatedAt(item?.raw?.createdAt || item?.raw?.date || "");
        setContentText(JSON.stringify(data, null, 2));
        setOriginalJobContentText("");
      } else {
        const parsedContent = getEditableJobContent(data);
        const nextContentText = JSON.stringify(parsedContent || {}, null, 2);

        setTitle(data?.title || parsedContent?.title || item.title || "");
        setCreatedAt(data?.createdAt || "");
        setAdvertisementNo(
          data?.advertisementNo ||
            data?.advertisement_no ||
            parsedContent?.advertisementNo ||
            parsedContent?.advertisement_no ||
            ""
        );
        setContentText(nextContentText);
        setOriginalJobContentText(nextContentText);
      }
    } catch (err) {
      setError(err.message || "Failed to load details.");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCreate = async () => {
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      if (isUserMode) {
        if (!userUsername.trim() || !userEmailId.trim() || !userPhone.trim() || !userPassword.trim()) {
          throw new Error("Username, email, phone and password are required.");
        }

        const response = await apiFetch("/users", {
          method: "POST",
          auth: true,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: userUsername.trim(),
            emailId: userEmailId.trim(),
            phone: userPhone.trim(),
            password: userPassword,
            role: userRole,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (response.status === 409) {
          throw new Error(data?.message || "User creation failed.");
        }
        if (!response.ok) throw new Error(data?.message || "User creation failed.");
        setMessage(`User created: ${data?.username || userUsername}`);
      } else if (isQuestionMode) {
        const payload = parseContentOrThrow();
        const questions = normalizeBulkQuestionsPayload(payload);

        if (!questions.length) {
          throw new Error("Questions list is required");
        }

        for (const question of questions) {
          const validationMessage = validateBulkQuestion(question);
          if (validationMessage) {
            throw new Error(validationMessage);
          }
        }

        const requestBody = Array.isArray(payload) ? payload : { questions };
        const response = await apiFetch("/questions/bulk", {
          method: "POST",
          auth: true,
          skipUnauthorizedHandler: true,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        });
        const data = await response.json().catch(() => ({}));

        if (response.status === 201) {
          setMessage(buildBulkUploadMessage(data));
        } else if (response.status === 400) {
          throw new Error(data?.message || "Invalid JSON payload");
        } else if (response.status === 401) {
          throw new Error("Session expired. Please login again.");
        } else if (response.status === 403) {
          throw new Error("You do not have admin permission to upload questions.");
        } else if (response.status >= 500) {
          throw new Error(
            "Upload failed due to server error. Some questions may already be saved. Refresh and retry."
          );
        } else if (!response.ok) {
          throw new Error(getApiErrorMessage({ ...new Error(), data }, "Question upload failed."));
        }
      } else {
        const contentObject = parseContentOrThrow();

        if (isQuizMode) {
          const payload = { content: contentObject };
          if (title.trim()) payload.title = title.trim();
          if (createdAt.trim()) payload.createdAt = createdAt.trim();

          const response = await apiFetch("/api/current-affairs-quizzes", {
            method: "POST",
            auth: true,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data?.message || "Create failed.");
          setMessage(`Created: ${data?.title || "Quiz"}`);
        } else {
          const data = await masterJobsActionApi.adminSave({
            label: selectedSection.jobLabel,
            payload: contentObject,
          });
          setMessage(
            data?.message ||
              (data?.action === "created"
                ? `Master job created: ${data?.title || data?.jobId || ""}`
                : "Master job saved.")
          );
        }
      }

      await loadList();
      resetForm();
    } catch (err) {
      setError(err.message || "Create failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedId) {
      setError("Select an item first to update.");
      return;
    }

    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      if (isUserMode) {
        if (!userUsername.trim() || !userEmailId.trim() || !userPhone.trim()) {
          throw new Error("Username, email and phone are required.");
        }

        const payload = {
          username: userUsername.trim(),
          emailId: userEmailId.trim(),
          phone: userPhone.trim(),
          role: userRole,
        };
        if (userPassword.trim()) payload.password = userPassword;

        const response = await apiFetch(`/users/${selectedId}`, {
          method: "PUT",
          auth: true,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => ({}));
        if (response.status === 409) {
          throw new Error(data?.message || "User update failed.");
        }
        if (!response.ok) throw new Error(data?.message || "User update failed.");
        setMessage(`User updated: ${payload.username}`);
      } else if (isQuestionMode) {
        const payload = parseContentOrThrow();
        const response = await apiFetch(`/questions/${selectedId}`, {
          method: "PUT",
          auth: true,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.message || "Question update failed.");
        setMessage(`Question updated: ${data?.questionCode || selectedId}`);
      } else {
        const contentObject = parseContentOrThrow();

        if (isQuizMode) {
          const payload = { content: contentObject };
          if (title.trim()) payload.title = title.trim();
          if (createdAt.trim()) payload.createdAt = createdAt.trim();

          const response = await apiFetch(`/api/current-affairs-quizzes/${selectedId}`, {
            method: "PUT",
            auth: true,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data?.message || "Update failed.");
          setMessage(`Updated: ${data?.title || "Quiz"}`);
        } else {
          const data = await masterJobsActionApi.adminUpdate({
            id: selectedId,
            label: selectedSection.jobLabel,
            payload: contentObject,
          });
          setMessage(
            data?.message ||
              (data?.action === "updated"
                ? `Master job updated: ${data?.title || data?.jobId || ""}`
                : "Master job updated.")
          );
          setOriginalJobContentText(JSON.stringify(contentObject, null, 2));
        }
      }

      await loadList();
    } catch (err) {
      setError(err.message || "Update failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setPendingDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    const id = pendingDeleteId;

    setSubmitting(true);
    setError("");
    setMessage("");
    setPendingDeleteId(null);
    try {
      let response;
      if (isUserMode) {
        response = await apiFetch(`/users/${id}`, {
          method: "DELETE",
          auth: true,
        });
      } else if (isQuestionMode) {
        response = await apiFetch(`/questions/${id}`, {
          method: "DELETE",
          auth: true,
        });
      } else {
        const path = isQuizMode
          ? `/api/current-affairs-quizzes/${id}`
          : `/api/jobs/${id}`;
        response = await apiFetch(path, { method: "DELETE", auth: true });
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || "Delete failed.");
      setMessage(data?.message || "Deleted.");
      if (selectedId === id) resetForm();
      await loadList();
    } catch (err) {
      setError(err.message || "Delete failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateNow = async () => {
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const response = await apiFetch("/api/current-affairs-quizzes/generate-now", {
        method: "POST",
        auth: true,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || "Generate failed.");
      setMessage(`Generated: ${data?.title || "Quiz"}`);
      await loadList();
    } catch (err) {
      setError(err.message || "Generate failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRunSchedulerNow = async () => {
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const data = await masterJobsActionApi.autoScrapeHomepage({
        force: autoForce,
        maxLinks: Number(autoMaxLinks || 0),
        maxPerCategory: Number(autoMaxPerCategory || 1),
      });
      setMessage(data?.message || "Homepage auto scrape completed.");
      await loadList();
    } catch (err) {
      if (
        handleAdminPermissionError(
          err,
          "Admin access is required to run homepage auto-scrape."
        )
      ) {
        return;
      }
      setError(err.message || "Scheduler run failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleScrapePreview = async () => {
    const normalizedUrl = scrapeUrl.trim();
    if (!normalizedUrl) {
      setError("Scrape URL is required.");
      return;
    }

    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const data = await masterJobsActionApi.scrape({ url: normalizedUrl });
      const rawContent = data?.content ?? data;
      const parsedContent =
        typeof rawContent === "string" ? JSON.parse(rawContent) : rawContent;

      setTitle(data?.title || parsedContent?.title || "");
      setContentText(JSON.stringify(parsedContent || {}, null, 2));
      setOriginalJobContentText("");
      setMessage("Scrape preview loaded into the editor.");
    } catch (err) {
      if (
        handleAdminPermissionError(
          err,
          "Admin access is required to preview scraped jobs."
        )
      ) {
        return;
      }
      setError(err.message || "Unable to preview scrape.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAiDirectPreview = async () => {
    const normalizedUrl = scrapeUrl.trim();
    if (!normalizedUrl) {
      setError("Scrape URL is required.");
      return;
    }

    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const data = await masterJobsActionApi.aiDirect(normalizedUrl);
      setContentText(JSON.stringify(data, null, 2));
      setTitle(data?.title || "");
      setOriginalJobContentText("");
      setMessage("AI direct response loaded into the editor.");
    } catch (err) {
      if (
        handleAdminPermissionError(
          err,
          "Admin access is required to run AI direct job extraction."
        )
      ) {
        return;
      }
      setError(err.message || "Unable to run AI direct.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleScrapeAndSave = async () => {
    const normalizedUrl = scrapeUrl.trim();
    const normalizedLabel = (scrapeLabel || selectedSection.jobLabel || "").trim();

    if (!normalizedUrl) {
      setError("Scrape URL is required.");
      return;
    }

    if (!normalizedLabel) {
      setError("A label is required to save scraped jobs.");
      return;
    }

    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const data = await masterJobsActionApi.scrapeAndSave({
        url: normalizedUrl,
        label: normalizedLabel,
        force: scrapeForce,
      });
      setMessage(data?.message || "Scraped job saved successfully.");
      await loadList();
    } catch (err) {
      if (
        handleAdminPermissionError(
          err,
          "Admin access is required to scrape and save jobs."
        )
      ) {
        return;
      }
      setError(err.message || "Unable to scrape and save.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenJobPreview = () => {
    try {
      const contentObject = parseContentOrThrow();
      setJobPreviewData(
        buildJobPreviewPayload({
          contentObject,
          title: title.trim(),
          advertisementNo: advertisementNo.trim(),
        })
      );
      setEditingPreviewSection(null);
      setPreviewSectionText("");
      setPreviewSectionError("");
      setIsJobPreviewOpen(true);
    } catch (err) {
      setError(err.message || "Unable to open preview.");
    }
  };

  const handleCloseJobPreview = () => {
    setIsJobPreviewOpen(false);
    setEditingPreviewSection(null);
    setPreviewSectionText("");
    setPreviewSectionError("");
  };

  const handleRemovePreviewSection = (sectionKey) => {
    setJobPreviewData((current) => {
      if (!current || typeof current !== "object") return current;
      const next = { ...current };
      delete next[sectionKey];
      return next;
    });

    if (editingPreviewSection === sectionKey) {
      setEditingPreviewSection(null);
      setPreviewSectionText("");
      setPreviewSectionError("");
    }
  };

  const handleEditPreviewSection = (sectionKey, value) => {
    setEditingPreviewSection(sectionKey);
    setPreviewSectionText(
      typeof value === "string" ? value : JSON.stringify(value, null, 2)
    );
    setPreviewSectionError("");
  };

  const handleApplyPreviewSection = () => {
    if (!editingPreviewSection) return;

    const currentValue = jobPreviewData?.[editingPreviewSection];
    let nextValue = previewSectionText;

    if (typeof currentValue !== "string") {
      try {
        nextValue = JSON.parse(previewSectionText);
      } catch {
        setPreviewSectionError("This section expects valid JSON.");
        return;
      }
    }

    setJobPreviewData((current) => ({
      ...(current || {}),
      [editingPreviewSection]: nextValue,
    }));
    setEditingPreviewSection(null);
    setPreviewSectionText("");
    setPreviewSectionError("");
  };

  const handleApplyPreviewChanges = () => {
    if (!jobPreviewData || typeof jobPreviewData !== "object") return;

    const nextContentText = JSON.stringify(jobPreviewData, null, 2);
    setContentText(nextContentText);
    setTitle(jobPreviewData?.title || title);
    setAdvertisementNo(
      jobPreviewData?.advertisement_no || jobPreviewData?.advertisementNo || advertisementNo
    );
    handleCloseJobPreview();
  };

  return (
    <div className="ca-admin-page">
      <div className="ca-admin-header">
        <h1>{`Admin ${activeAdminPage.label} Manager`}</h1>
        <div className="ca-admin-filter">
          <label htmlFor="admin-section">Section</label>
          <select
            id="admin-section"
            value={activeAdminPage.key}
            onChange={(e) => {
              const nextPage = e.target.value;
              if (nextPage === "jobs") {
                navigate(
                  `/admin/dashboard/jobs/${isJobMode ? selectedSection.key : JOB_SECTIONS[0].key}`
                );
                return;
              }
              navigate(`/admin/dashboard/${nextPage}`);
            }}
          >
            {ADMIN_PAGES.map((page) => (
              <option key={page.key} value={page.key}>
                {page.label}
              </option>
            ))}
          </select>
        </div>
        <div className="ca-admin-actions">
          <button
            type="button"
            onClick={() => navigate("/admin/users")}
            className="ca-admin-btn"
          >
            Manage Users
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/quiz-attempt-stats")}
            className="ca-admin-btn"
          >
            Quiz Attempt Stats
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/syllabus")}
            className="ca-admin-btn"
          >
            Manage Syllabus
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/analytics")}
            className="ca-admin-btn"
          >
            Analytics
          </button>
          {isQuizMode ? (
            <button
              type="button"
              onClick={handleGenerateNow}
              disabled={submitting}
              className="ca-admin-btn ca-admin-btn-primary"
            >
              Generate Today Quiz
            </button>
          ) : null}
          {!isQuizMode && !isUserMode && !isQuestionMode ? (
            <button
              type="button"
              onClick={handleRunSchedulerNow}
              disabled={submitting}
              className="ca-admin-btn ca-admin-btn-primary"
            >
              Auto Scrape Homepage
            </button>
          ) : null}
          <button
            type="button"
            onClick={resetForm}
            disabled={submitting}
            className="ca-admin-btn"
          >
            New
          </button>
          <button
            type="button"
            onClick={loadList}
            disabled={loadingList || submitting}
            className="ca-admin-btn"
          >
            Refresh
          </button>
        </div>
      </div>

      {isJobMode ? (
        <div className="ca-admin-subnav">
          {JOB_SECTIONS.map((section) => (
            <button
              key={section.key}
              type="button"
              onClick={() => navigate(`/admin/dashboard/jobs/${section.key}`)}
              className={`ca-admin-subnav-btn ${
                selectedSection.key === section.key ? "active" : ""
              }`}
            >
              {section.label}
            </button>
          ))}
        </div>
      ) : null}

      {isQuestionMode ? (
        <div className="ca-admin-question-filters">
          <div className="ca-admin-filter-group">
            <label htmlFor="admin-question-subject-filter">Subject</label>
            <select
              id="admin-question-subject-filter"
              value={selectedQuestionSubject}
              onChange={(e) => setSelectedQuestionSubject(e.target.value)}
            >
              <option value="">Select Subject</option>
              {questionSubjects.map((subject) => (
                <option key={subject} value={subject}>
                  {subject}
                </option>
              ))}
            </select>
          </div>
          <div className="ca-admin-filter-group">
            <label htmlFor="admin-question-topic-filter">Topic</label>
            <select
              id="admin-question-topic-filter"
              value={selectedQuestionTopic}
              onChange={(e) => setSelectedQuestionTopic(e.target.value)}
              disabled={!selectedQuestionSubject}
            >
              <option value="">Select Topic</option>
              {questionTopics.map((topic) => (
                <option key={topic} value={topic}>
                  {topic}
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : null}

      {isQuizMode ? (
        <section
          className="ca-admin-tool-card ca-admin-scheduler-card"
          aria-label="Daily quiz scheduler settings"
        >
          <div className="ca-admin-scheduler-row">
            <div>
              <h3>Daily Quiz Scheduler</h3>
              <p className="ca-admin-tool-copy">
                Enable or disable automatic daily quiz generation for the scheduler.
              </p>
            </div>
            <span
              className={`ca-admin-status-badge ${
                schedulerEnabled ? "enabled" : "disabled"
              } ${schedulerLoading ? "loading" : ""}`}
            >
              {schedulerLoading ? "Checking..." : schedulerEnabled ? "Enabled" : "Disabled"}
            </span>
          </div>

          <div className="ca-admin-scheduler-controls">
            <label className="ca-admin-switch">
              <input
                type="checkbox"
                checked={schedulerEnabled}
                disabled={schedulerLoading || schedulerUpdating}
                onChange={(e) => updateSchedulerStatus(e.target.checked)}
                aria-label="Toggle daily quiz scheduler"
              />
              <span className="ca-admin-switch-slider" aria-hidden="true" />
              <span className="ca-admin-switch-label">
                {schedulerUpdating
                  ? "Saving..."
                  : schedulerEnabled
                  ? "Turn scheduler off"
                  : "Turn scheduler on"}
              </span>
            </label>

            <button
              type="button"
              onClick={loadSchedulerStatus}
              disabled={schedulerLoading || schedulerUpdating}
              className="ca-admin-btn"
            >
              Refresh Status
            </button>
          </div>
        </section>
      ) : null}

      {message ? <p className="ca-admin-msg ok">{message}</p> : null}
      {error ? <p className="ca-admin-msg err">{error}</p> : null}

      <div className="ca-admin-grid">
        <section className="ca-admin-list-card">
          <h2>{selectedSection.label} List</h2>
          {loadingList ? <p>Loading list...</p> : null}
          {!loadingList && isQuestionMode && (!selectedQuestionSubject || !selectedQuestionTopic) ? (
            <p>Select a subject and topic to view questions.</p>
          ) : null}
          {!loadingList &&
          !(isQuestionMode && (!selectedQuestionSubject || !selectedQuestionTopic)) &&
          visibleItems.length === 0 ? (
            <p>No items found.</p>
          ) : null}
          <ul className="ca-admin-list">
            {visibleItems.map((item) => {
              const active = item.id === selectedId;
              return (
                <li key={item.id} className={active ? "active" : ""}>
                  <button
                    type="button"
                    onClick={() => loadDetail(item)}
                    className="ca-admin-list-title"
                  >
                    {item.title}
                  </button>
                  <div className="ca-admin-list-meta">
                    <span>#{item.id}</span>
                    <button
                      type="button"
                      onClick={() => handleDelete(item.id)}
                      disabled={submitting}
                      className="ca-admin-delete-btn"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="ca-admin-form-card">
          <h2>{selectedItem ? `Edit ${selectedSection.label}` : `Create ${selectedSection.label}`}</h2>
          {loadingDetail ? <p>Loading details...</p> : null}

          {isUserMode ? (
            <>
              <label htmlFor="admin-user-username">Username</label>
              <input
                id="admin-user-username"
                type="text"
                value={userUsername}
                onChange={(e) => setUserUsername(e.target.value)}
                placeholder="Username"
              />

              <label htmlFor="admin-user-email">Email</label>
              <input
                id="admin-user-email"
                type="email"
                value={userEmailId}
                onChange={(e) => setUserEmailId(e.target.value)}
                placeholder="Email ID"
              />

              <label htmlFor="admin-user-phone">Phone</label>
              <input
                id="admin-user-phone"
                type="tel"
                value={userPhone}
                onChange={(e) => setUserPhone(e.target.value)}
                placeholder="Phone"
              />

              <label htmlFor="admin-user-role">Role</label>
              <select
                id="admin-user-role"
                value={userRole}
                onChange={(e) => setUserRole(e.target.value)}
              >
                <option value="STUDENT">Student</option>
                <option value="ADMIN">Admin</option>
              </select>

              <label htmlFor="admin-user-password">
                Password {selectedItem ? "(leave blank to keep same)" : ""}
              </label>
              <input
                id="admin-user-password"
                type="password"
                value={userPassword}
                onChange={(e) => setUserPassword(e.target.value)}
                placeholder={selectedItem ? "Optional on update" : "Required"}
              />
            </>
          ) : isQuestionMode ? (
            <>
              <label htmlFor="admin-question-json-upload">Upload Question JSON File</label>
              <input
                key={jsonUploadKey}
                id="admin-question-json-upload"
                type="file"
                accept=".json,application/json"
                onChange={handleQuestionJsonUpload}
              />

              <label htmlFor="admin-question-json">Question JSON</label>
              <textarea
                id="admin-question-json"
                value={contentText}
                onChange={(e) => setContentText(e.target.value)}
                rows={20}
                spellCheck={false}
              />
              <div className="ca-admin-actions ca-admin-job-editor-actions">
                <button
                  type="button"
                  onClick={handleOpenQuestionPreview}
                  disabled={submitting}
                  className="ca-admin-btn"
                >
                  Preview Questions
                </button>
              </div>
            </>
          ) : isQuizMode ? (
            <>
              <label htmlFor="admin-title">Title</label>
              <input
                id="admin-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title"
              />

              <label htmlFor="admin-date">Date (YYYY-MM-DD)</label>
              <input
                id="admin-date"
                type="date"
                value={createdAt}
                onChange={(e) => setCreatedAt(e.target.value)}
              />

              <label htmlFor="admin-content">Content JSON</label>
              <textarea
                id="admin-content"
                value={contentText}
                onChange={(e) => setContentText(e.target.value)}
                rows={18}
                spellCheck={false}
              />
            </>
          ) : (
            <>
              {!selectedItem ? (
                <div className="ca-admin-tool-card">
                  <h3>Master Job Scraper</h3>
                  <p className="ca-admin-tool-copy">
                    Use the new master job scraping endpoints to preview, extract, save, or auto-scrape jobs.
                  </p>

                  <label htmlFor="admin-scrape-url">Source URL</label>
                  <input
                    id="admin-scrape-url"
                    type="url"
                    value={scrapeUrl}
                    onChange={(e) => setScrapeUrl(e.target.value)}
                    placeholder="https://example.com/job-post"
                  />

                  <label htmlFor="admin-scrape-label">Save Label</label>
                  <input
                    id="admin-scrape-label"
                    type="text"
                    value={scrapeLabel}
                    onChange={(e) => setScrapeLabel(e.target.value)}
                    placeholder="Latest jobs"
                  />

                  <label className="ca-admin-checkbox">
                    <input
                      type="checkbox"
                      checked={scrapeForce}
                      onChange={(e) => setScrapeForce(e.target.checked)}
                    />
                    <span>Force scrape-and-save</span>
                  </label>

                  <div className="ca-admin-actions">
                    <button
                      type="button"
                      onClick={handleScrapePreview}
                      disabled={submitting}
                      className="ca-admin-btn"
                    >
                      Preview Scrape
                    </button>
                    <button
                      type="button"
                      onClick={handleAiDirectPreview}
                      disabled={submitting}
                      className="ca-admin-btn"
                    >
                      AI Direct
                    </button>
                    <button
                      type="button"
                      onClick={handleScrapeAndSave}
                      disabled={submitting}
                      className="ca-admin-btn ca-admin-btn-primary"
                    >
                      Scrape And Save
                    </button>
                  </div>

                  <div className="ca-admin-auto-grid">
                    <div>
                      <label htmlFor="admin-auto-max-links">Max Links</label>
                      <input
                        id="admin-auto-max-links"
                        type="number"
                        min="0"
                        value={autoMaxLinks}
                        onChange={(e) => setAutoMaxLinks(e.target.value)}
                      />
                    </div>
                    <div>
                      <label htmlFor="admin-auto-max-per-category">Max Per Category</label>
                      <input
                        id="admin-auto-max-per-category"
                        type="number"
                        min="1"
                        value={autoMaxPerCategory}
                        onChange={(e) => setAutoMaxPerCategory(e.target.value)}
                      />
                    </div>
                  </div>

                  <label className="ca-admin-checkbox">
                    <input
                      type="checkbox"
                      checked={autoForce}
                      onChange={(e) => setAutoForce(e.target.checked)}
                    />
                    <span>Force homepage auto-scrape</span>
                  </label>
                </div>
              ) : (
                <p className="ca-admin-tool-copy">
                  Edit the selected job JSON below. Preview opens the real job layout in a modal, where you can update or remove individual sections.
                </p>
              )}

              <label htmlFor="admin-content">Content JSON</label>
              <textarea
                id="admin-content"
                value={contentText}
                onChange={(e) => setContentText(e.target.value)}
                rows={18}
                spellCheck={false}
              />

              {selectedItem ? (
                <div className="ca-admin-actions ca-admin-job-editor-actions">
                  <button
                    type="button"
                    onClick={handleOpenJobPreview}
                    disabled={submitting || !selectedId}
                    className="ca-admin-btn"
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    onClick={handleUpdate}
                    disabled={submitting || !selectedId || !hasJobJsonChanges}
                    className="ca-admin-btn ca-admin-btn-primary"
                  >
                    {submitting ? "Updating..." : "Update Job"}
                  </button>
                </div>
              ) : null}
            </>
          )}

          <div className="ca-admin-actions">
            {!hasSelectedExistingJob ? (
              <button
                type="button"
                onClick={handleCreate}
                disabled={submitting}
                className="ca-admin-btn ca-admin-btn-primary"
              >
                {isUserMode ? "Create User" : isQuestionMode ? "Create Question" : "Create"}
              </button>
            ) : null}
            {!isJobMode ? (
              <button
                type="button"
                onClick={handleUpdate}
                disabled={submitting || !selectedId}
                className="ca-admin-btn"
              >
                {isUserMode ? "Update User" : isQuestionMode ? "Update Question" : "Update Selected"}
              </button>
            ) : null}
          </div>
        </section>
      </div>

      {isJobPreviewOpen && jobPreviewData ? (
        <div className="ca-admin-preview-overlay" role="dialog" aria-modal="true">
          <div className="ca-admin-preview-card">
            <div className="ca-admin-preview-topbar">
              <div>
                <p className="ca-admin-confirm-eyebrow">Job Preview</p>
                <h2>Preview And Edit Sections</h2>
              </div>
              <button type="button" className="ca-admin-btn" onClick={handleCloseJobPreview}>
                Close
              </button>
            </div>

            <div className="ca-admin-preview-body">
              <div className="ca-admin-preview-main">
                <JobDetailsView
                  job={jobPreviewData}
                  showHelmet={false}
                  renderSectionActions={({ key, value }) => (
                    <>
                      <button
                        type="button"
                        className="ca-admin-btn"
                        onClick={() => handleEditPreviewSection(key, value)}
                      >
                        Update Section
                      </button>
                      <button
                        type="button"
                        className="ca-admin-btn ca-admin-btn-danger"
                        onClick={() => handleRemovePreviewSection(key)}
                      >
                        Remove Section
                      </button>
                    </>
                  )}
                />
              </div>

              <aside className="ca-admin-preview-sidebar">
                <h3>Section Editor</h3>
                <p className="ca-admin-tool-copy">
                  Select a section from the preview to edit or remove it.
                </p>
                {editingPreviewSection ? (
                  <>
                    <label htmlFor="admin-preview-section-editor">
                      {editingPreviewSection}
                    </label>
                    <textarea
                      id="admin-preview-section-editor"
                      value={previewSectionText}
                      onChange={(e) => setPreviewSectionText(e.target.value)}
                      rows={14}
                      spellCheck={false}
                    />
                    {previewSectionError ? (
                      <p className="ca-admin-msg err">{previewSectionError}</p>
                    ) : null}
                    <div className="ca-admin-actions">
                      <button
                        type="button"
                        className="ca-admin-btn ca-admin-btn-primary"
                        onClick={handleApplyPreviewSection}
                      >
                        Submit Section
                      </button>
                      <button
                        type="button"
                        className="ca-admin-btn"
                        onClick={() => {
                          setEditingPreviewSection(null);
                          setPreviewSectionText("");
                          setPreviewSectionError("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="ca-admin-empty-state">
                    Use the buttons under each preview section to edit that part of the job JSON.
                  </p>
                )}
              </aside>
            </div>

            <div className="ca-admin-actions">
              <button
                type="button"
                className="ca-admin-btn ca-admin-btn-primary"
                onClick={handleApplyPreviewChanges}
              >
                Submit Preview Changes
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isQuestionPreviewOpen ? (
        <div className="ca-admin-preview-overlay" role="dialog" aria-modal="true">
          <div className="ca-admin-preview-card">
            <div className="ca-admin-preview-topbar">
              <div>
                <p className="ca-admin-confirm-eyebrow">Question Preview</p>
                <h2>Preview And Edit MCQs</h2>
              </div>
              <button type="button" className="ca-admin-btn" onClick={handleCloseQuestionPreview}>
                Close
              </button>
            </div>

            <div className="ca-admin-preview-body">
              <div className="ca-admin-preview-main">
                <div className="ca-admin-question-preview-list">
                  {questionPreviewItems.map((question, index) => {
                    const choices = getQuestionChoices(question);
                    const explanationText = getQuestionExplanationText(question);
                    const { diagramType, diagramData } = getQuestionExplanationDiagram(question);
                    return (
                      <article
                        key={`${question?.questionCode || "question"}-${index}`}
                        className="ca-admin-question-card"
                      >
                        <div className="ca-admin-question-card-topbar">
                          <div>
                            <p className="ca-admin-confirm-eyebrow">{`MCQ ${index + 1}`}</p>
                            <h3>{question?.questionText || question?.question || "Untitled question"}</h3>
                          </div>
                          <div className="ca-admin-actions">
                            <button
                              type="button"
                              className="ca-admin-btn"
                              onClick={() => handleEditPreviewQuestion(index)}
                            >
                              Update MCQ
                            </button>
                            <button
                              type="button"
                              className="ca-admin-btn ca-admin-btn-danger"
                              onClick={() => handleRemovePreviewQuestion(index)}
                            >
                              Remove MCQ
                            </button>
                          </div>
                        </div>

                        <div className="ca-admin-question-meta-grid">
                          <div className="ca-admin-question-meta-pill">
                            <span>Code</span>
                            <strong>{formatQuestionMetaLabel(question?.questionCode)}</strong>
                          </div>
                          <div className="ca-admin-question-meta-pill">
                            <span>Subject</span>
                            <strong>{formatQuestionMetaLabel(question?.subject)}</strong>
                          </div>
                          <div className="ca-admin-question-meta-pill">
                            <span>Topic</span>
                            <strong>{formatQuestionMetaLabel(question?.topic)}</strong>
                          </div>
                          <div className="ca-admin-question-meta-pill">
                            <span>Type</span>
                            <strong>{formatQuestionMetaLabel(question?.type)}</strong>
                          </div>
                          <div className="ca-admin-question-meta-pill">
                            <span>Status</span>
                            <strong>{formatQuestionMetaLabel(question?.status)}</strong>
                          </div>
                          <div className="ca-admin-question-meta-pill">
                            <span>Correct Answer</span>
                            <strong>{getQuestionCorrectAnswerSummary(question)}</strong>
                          </div>
                        </div>

                        <div className="ca-admin-question-choice-shell">
                          <h4>Choices</h4>
                          <div className="ca-admin-question-choice-list">
                            {choices.map((choice, choiceIndex) => (
                              <div
                                key={`choice-${index}-${choiceIndex}`}
                                className="ca-admin-question-choice-item"
                              >
                                <span className="ca-admin-question-choice-index">
                                  {String.fromCharCode(65 + choiceIndex)}
                                </span>
                                <div>
                                  <strong>{getQuestionChoiceLabel(choice, choiceIndex)}</strong>
                                  {choice && typeof choice === "object" && choice?.id ? (
                                    <p>{`Choice ID: ${choice.id}`}</p>
                                  ) : null}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        {explanationText || (diagramType === "DIRECTION_SENSE_SVG" && diagramData) ? (
                          <QuestionExplanationPreview
                            text={explanationText}
                            diagramType={diagramType}
                            diagramData={diagramData}
                          />
                        ) : null}
                      </article>
                    );
                  })}
                </div>
              </div>

              <aside className="ca-admin-preview-sidebar">
                <h3>MCQ Editor</h3>
                <p className="ca-admin-tool-copy">
                  Use the buttons on each MCQ to update or remove it before upload.
                </p>
                {typeof editingPreviewQuestionIndex === "number" ? (
                  <>
                    <label htmlFor="admin-preview-question-editor">
                      {`MCQ ${editingPreviewQuestionIndex + 1}`}
                    </label>
                    <textarea
                      id="admin-preview-question-editor"
                      value={previewQuestionText}
                      onChange={(e) => setPreviewQuestionText(e.target.value)}
                      rows={18}
                      spellCheck={false}
                    />
                    {previewQuestionError ? (
                      <p className="ca-admin-msg err">{previewQuestionError}</p>
                    ) : null}
                    <div className="ca-admin-actions">
                      <button
                        type="button"
                        className="ca-admin-btn ca-admin-btn-primary"
                        onClick={handleApplyPreviewQuestion}
                      >
                        Apply MCQ Update
                      </button>
                      <button
                        type="button"
                        className="ca-admin-btn"
                        onClick={() => {
                          setEditingPreviewQuestionIndex(null);
                          setPreviewQuestionText("");
                          setPreviewQuestionError("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="ca-admin-tool-copy">
                    Select an MCQ from the preview list to edit its JSON.
                  </p>
                )}
              </aside>
            </div>

            <div className="ca-admin-actions">
              <button
                type="button"
                className="ca-admin-btn ca-admin-btn-primary"
                onClick={handleApplyQuestionPreviewChanges}
              >
                Apply Preview Changes
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {pendingDeleteId ? (
        <div className="ca-admin-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="ca-admin-delete-title">
          <div className="ca-admin-confirm-card">
            <p className="ca-admin-confirm-eyebrow">Confirm Delete</p>
            <h2 id="ca-admin-delete-title">Delete this item?</h2>
            <p className="ca-admin-confirm-copy">
              This action will permanently remove the selected item from the current admin section.
            </p>
            <div className="ca-admin-actions">
              <button
                type="button"
                className="ca-admin-btn"
                onClick={() => setPendingDeleteId(null)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ca-admin-btn ca-admin-btn-primary"
                onClick={confirmDelete}
                disabled={submitting}
              >
                {submitting ? "Deleting..." : "Delete Item"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <Toast message={toastMessage} onClose={() => setToastMessage("")} />
    </div>
  );
}

export default CurrentAffairsQuizAdmin;
